import { Organization } from '../../types/organization.types';

/**
 * Generates a URL for previewing an email template with organization and contact data
 *
 * @param templateName The name of the email template (e.g., "home_value_1")
 * @param organization The organization data
 * @param contactFirstName Optional contact first name for personalization
 * @param subjectLine Optional subject line for the email
 * @returns A URL string for the email template preview
 */
export const generateEmailTemplateUrl = (
  templateName: string,
  organization: Organization,
  contactFirstName?: string,
  subjectLine?: string
): string => {
  // Base URL for email templates
  const baseUrl = 'https://speculo-email-template.web.app/';

  // Create URL params object
  const params: Record<string, string> = {
    template: templateName,
    organization_name: organization.org_name || '',
    combined_name: organization.org_name || '',
  };

  // Add contact first name if provided
  if (contactFirstName) {
    params.contact_first_name = contactFirstName;
  }

  // Add branding if available
  if (organization.branding?.primaryColor) {
    params['branding.primaryColor'] = organization.branding.primaryColor;
  }

  // Add logo URL if available
  if (organization.logoUrl) {
    params.logoUrl = organization.logoUrl;
  }

  // Add organization phone if available
  if (organization.org_phone) {
    params.organization_phone = organization.org_phone;
  }

  // Add organization address if available
  if (organization.client_address) {
    if (organization.client_address.street) {
      params['organization_address.street'] = organization.client_address.street;
    }
    if (organization.client_address.city) {
      params['organization_address.city'] = organization.client_address.city;
    }
    if (organization.client_address.state) {
      params['organization_address.state'] = organization.client_address.state;
    }
    if (organization.client_address.zip) {
      params['organization_address.zip'] = organization.client_address.zip;
    }
  }

  // Add organization links if available
  if (organization.org_links) {
    Object.entries(organization.org_links).forEach(([key, value]) => {
      params[`org_links.${key}`] = value;
    });
  }

  // Add subject line if provided
  if (subjectLine) {
    params.subject_line = subjectLine;
  }

  // Add tracking and unsubscribe URLs (placeholders)
  params.tracking_url = 'https://example.com/track';
  params.unsubscribe_url = 'https://example.com/unsubscribe';

  // Convert params object to URL query string
  const queryString = Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return `${baseUrl}?${queryString}`;
};
